// TES-1120 — the medical question case on the doctor's side, the pure parts.
//
// Pure on purpose (no React, no fetch) so `node --test` can run it.
import {
  DOCTOR_QUESTION_MEDICAL_ACTION_LABELS,
  DOCTOR_QUESTION_STATUS_LABELS,
  type DoctorQuestionSummary,
  type MedicalReviewCaseSummary,
  type NoticeMode,
  type Role,
} from "@care-by-ella/contracts";

/**
 * A patient's medical question, escalated by the coach or Ella Support — as
 * opposed to a weekly check-in case. Mirrors MEDICAL_QUESTION_CASE_SOURCES.
 */
export function isQuestionCase(
  c: Pick<MedicalReviewCaseSummary, "source">,
): boolean {
  return c.source !== "weekly_checkin";
}

/**
 * The SLA as the case shows it. A question is due two working days after
 * the escalation (Ö-341) and is late once that has passed; a weekly
 * check-in case has no SLA until the medical protocol gives one.
 */
export function caseSla(
  c: Pick<MedicalReviewCaseSummary, "source" | "slaDueAt" | "status">,
  now: Date,
): { dueAt: string | null; overdue: boolean } {
  if (!c.slaDueAt) return { dueAt: null, overdue: false };
  return {
    dueAt: c.slaDueAt,
    overdue:
      c.status === "open" && now.getTime() > new Date(c.slaDueAt).getTime(),
  };
}

/**
 * "Svara som medicinskt besked" is the doctor's own act: a doctor, outside
 * the admin's read-only view, on a question whose originals she has opened
 * in this view. The server holds the same rules (doctor-only route; 409
 * without a fresh opening).
 */
export function canAnswerCase(input: {
  c: Pick<MedicalReviewCaseSummary, "source">;
  role: Role | null | undefined;
  readOnly: boolean;
  opened: boolean;
}): boolean {
  return (
    input.role === "doctor" &&
    !input.readOnly &&
    input.opened &&
    isQuestionCase(input.c)
  );
}

/**
 * TES-1332 (TES-1331, Emelie 2026-10-13) — "Besked till patienten" in one of
 * exactly two modes, a radio: "Besked – inget svar behövs" (`info`) or
 * "Besked med fråga – patienten ska svara" (`question`, with "Din fråga").
 * The generic task ("Patienten behöver göra något — skapa en uppgift i
 * appen") and its fields are gone from this flow.
 */
export interface AnswerDraft {
  mode: NoticeMode;
  body: string;
  question: string;
}

/**
 * The request a complete draft sends, or null while it is incomplete: the
 * notice needs its text, and "Besked med fråga" its question.
 */
export function answerRequest(
  d: AnswerDraft,
): { body: string; question?: { text: string } } | null {
  const body = d.body.trim();
  if (!body) return null;
  if (d.mode === "info") return { body };
  const text = d.question.trim();
  if (!text) return null;
  return { body, question: { text } };
}

export const EMPTY_ANSWER_DRAFT: AnswerDraft = {
  mode: "info",
  body: "",
  question: "",
};

/** A day and time in Stockholm, as the question and its answer show it. */
export function stockholmDateTime(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * TES-1149 / TES-1332 — a doctor's question's status, in the decision's
 * words: "Inväntar patientsvar", "Besvarad" or "Hanterad".
 */
export function doctorQuestionStatusText(
  q: Pick<DoctorQuestionSummary, "status">,
): string {
  return DOCTOR_QUESTION_STATUS_LABELS[q.status];
}

/**
 * TES-1332 / TES-1335 — how the doctor handled her answer, after "Hanterad
 * <time> av <name>": nothing for "Hanterat – inget nytt besked behövs", the
 * new notice, or the structured action that took it over. Proposed copy,
 * except the actions' names.
 */
export function handledHowText(
  q: Pick<DoctorQuestionSummary, "handledVia" | "handledAction">,
): string {
  if (q.handledVia === "new_notice") return " med ett nytt besked";
  if (q.handledVia === "medical_action" && q.handledAction) {
    return ` genom åtgärden ”${DOCTOR_QUESTION_MEDICAL_ACTION_LABELS[q.handledAction]}”`;
  }
  return "";
}

/**
 * TES-1335 — "Återkalla frågan" is offered on a question she has not
 * answered, to the doctor outside the admin's read-only view. The server
 * holds the same rule (doctor-only route; 409 once answered).
 */
export function canWithdrawQuestion(input: {
  q: Pick<DoctorQuestionSummary, "status">;
  role: Role | null | undefined;
  readOnly: boolean;
}): boolean {
  return (
    input.role === "doctor" && !input.readOnly && input.q.status === "waiting"
  );
}

/**
 * TES-1332 — how long her answer has waited for the doctor, for the queue
 * row: minutes, hours or days. Proposed copy.
 */
export function answerWaitingText(answeredAt: string, now: Date): string {
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - new Date(answeredAt).getTime()) / 60_000),
  );
  if (minutes < 60) return `Väntat ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Väntat ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Väntat ${days} ${days === 1 ? "dag" : "dagar"}`;
}

/**
 * TES-1147 — whether the doctor's free-text composer in the chat pane is
 * replaced by "Skicka besked". Only the doctor, only in the doctor
 * channel, and only once the thread has loaded empty: a patient without
 * doctor-chat history has no doctor chat (the server refuses the first
 * message with `doctor_chat_not_open`). A thread with history keeps its
 * composer while the patient's app is one the doctor channel is still open
 * for.
 *
 * TES-1145 — once the server reports the doctor chat closed for her (her
 * app's last reported contract, or DOCTOR_CHAT_CLOSED_FOR_ALL), the composer
 * is replaced whatever the history: her app shows the thread as read-only
 * history, and the server refuses the send.
 */
export function doctorComposerReplaced(input: {
  role: Role | null | undefined;
  channel: "doctor" | "coach";
  loaded: boolean;
  messageCount: number;
  doctorChatClosed?: boolean;
}): boolean {
  return (
    input.role === "doctor" &&
    input.channel === "doctor" &&
    input.loaded &&
    (input.messageCount === 0 || input.doctorChatClosed === true)
  );
}
